import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
	Users,
	Search,
	Link,
	Copy,
	Check,
	FileText,
	Building2,
	Loader2,
	Filter
} from 'lucide-react';
import { Card, CardContent } from '@hiring-intelligence/ui';
import { Button } from '@hiring-intelligence/ui';
import { Input } from '@hiring-intelligence/ui';
import { Badge } from '@hiring-intelligence/ui';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@hiring-intelligence/ui';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@hiring-intelligence/ui';

import { candidateApi, accessLinkApi } from '@/services/api';
import { useToast } from '@hiring-intelligence/ui';
import { useAuthStore } from '@/stores/auth';

export default function CandidatesPage() {
	const [searchQuery, setSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [linkDialogOpen, setLinkDialogOpen] = useState(false);
	const [selectedCandidateId, setSelectedCandidateId] = useState('');
	const [copiedToken, setCopiedToken] = useState('');
	const [generatedLink, setGeneratedLink] = useState('');
	const [existingLink, setExistingLink] = useState<{ url: string; expiresAt: string } | null>(null);
	const [loadingExisting, setLoadingExisting] = useState(false);


	const { toast } = useToast();
	const navigate = useNavigate();
	const user = useAuthStore((s) => s.user);

	const { data: candidatesData, isLoading } = useQuery({
		queryKey: ['all-candidates', statusFilter],
		queryFn: () => candidateApi.list(statusFilter !== 'all' ? statusFilter : undefined)
	});

	const generateLinkMutation = useMutation({
		mutationFn: (data: { sourcedCandidateId: string; jdType: string }) =>
			candidateApi.generateLink(data.sourcedCandidateId, data.jdType),
		onSuccess: (result: any) => {
			const url = `${process.env.PORTAL_URL}/c/${result.data.token}`;
			setGeneratedLink(url);
			toast({
				title: 'Link Generated',
				description: 'Candidate access link has been generated.'
			});
		},
		onError: (error: Error) => {
			toast({ title: 'Error', description: error.message, variant: 'destructive' });
		}
	});

	const handleGenerateLink = async (candidateId: string) => {
		setSelectedCandidateId(candidateId);
		setGeneratedLink('');
		setExistingLink(null);
		setLinkDialogOpen(true);

		// Check for existing non-expired candidate link
		const candidate = candidates.find((c: any) => c.id === candidateId);
		if (candidate?.hiringRequestId) {
			setLoadingExisting(true);
			try {
				const result = await accessLinkApi.listLinks(candidate.hiringRequestId);
				const activeLink = (result.data || []).find(
					(l: any) => l.type === 'candidate' && l.sourcedCandidateId === candidateId && new Date(l.expiresAt) > new Date()
				);
				if (activeLink) {
					const url = `${process.env.PORTAL_URL}/c/${activeLink.token}`;
					setExistingLink({ url, expiresAt: activeLink.expiresAt });
				}
			} catch {
				// Ignore errors, just show generate form
			} finally {
				setLoadingExisting(false);
			}
		}
	};

	const handleConfirmGenerate = () => {
		generateLinkMutation.mutate({ sourcedCandidateId: selectedCandidateId, jdType: 'blind' });
	};

	const handleCopyLink = async (link: string) => {
		await (globalThis as any).navigator.clipboard.writeText(link);
		setCopiedToken(link);
		setTimeout(() => setCopiedToken(''), 2000);
		toast({ title: 'Copied', description: 'Link copied to clipboard' });
	};

	const candidates = candidatesData?.data || [];
	const filteredCandidates = candidates.filter((c: any) =>
		c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
		c.currentTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
		c.currentCompany?.toLowerCase().includes(searchQuery.toLowerCase()) ||
		c.hiringRequest?.roleTitle?.toLowerCase().includes(searchQuery.toLowerCase())
	);

	const statusColors: Record<string, string> = {
		sourced: 'secondary',
		contacted: 'default',
		interviewing: 'default',
		hired: 'success',
		rejected: 'destructive'
	};

	return (
		<div className="p-4 md:p-8">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
				<div>
					<h1 className="text-2xl font-bold">Candidates</h1>
					<p className="text-muted-foreground">All sourced candidates across hiring requests</p>
				</div>
			</div>

			{/* Filters */}
			<div className="flex flex-col sm:flex-row gap-4 mb-6">
				<div className="relative flex-1 max-w-md">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder="Search candidates..."
						value={searchQuery}
						onChange={(e) => setSearchQuery((e.target as any).value)}
						className="pl-9"
					/>
				</div>
				<Select value={statusFilter} onValueChange={setStatusFilter}>
					<SelectTrigger className="w-full sm:w-40">
						<Filter className="mr-2 h-4 w-4" />
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All Status</SelectItem>
						<SelectItem value="sourced">Sourced</SelectItem>
						<SelectItem value="contacted">Contacted</SelectItem>
						<SelectItem value="interviewing">Interviewing</SelectItem>
						<SelectItem value="hired">Hired</SelectItem>
						<SelectItem value="rejected">Rejected</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{/* Candidates List */}
			{isLoading ? (
				<div className="flex items-center justify-center py-16">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			) : filteredCandidates.length > 0 ? (
				<div className="space-y-3">
					{filteredCandidates.map((candidate: any) => (
						<Card key={candidate.id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => navigate(`/candidates/${candidate.id}`)}>
						<CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
							<div className="flex-1 min-w-0">
									<div className="flex items-center gap-3">
										<div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
											<span className="text-sm font-medium text-primary">
												{candidate.name?.charAt(0).toUpperCase()}
											</span>
										</div>
										<div>
											<div className="font-medium">{candidate.name}</div>
											<div className="text-sm text-muted-foreground">
												{candidate.currentTitle} {candidate.currentCompany ? `at ${candidate.currentCompany}` : ''}
											</div>
										</div>
									</div>
									<div className="flex flex-wrap items-center gap-2 mt-2 ml-13">
										{user?.role === 'super_admin' && candidate.hiringRequest && (
											<Badge variant="outline" className="text-xs">
												<Building2 className="mr-1 h-3 w-3" />
												{candidate.hiringRequest.roleTitle}
												{candidate.hiringRequest.company?.name ? ` - ${candidate.hiringRequest.company.name}` : ''}
											</Badge>
										)}
											{candidate.status === 'hired' ? (
												<Badge variant="success">Hired</Badge>
											) : candidate.status === 'withdrawn' ? (
												<Badge variant="destructive">Withdrawn</Badge>
											) : candidate.recommended ? (
												<Badge variant="success">Recommended</Badge>
											) : (
												<Badge variant={statusColors[candidate.status] as any || 'secondary'}>
													{candidate.status}
												</Badge>
										)}
										{candidate.cvFileUrl && (
											<Badge variant="outline" className="text-xs">
												<FileText className="mr-1 h-3 w-3" />
												CV Uploaded
											</Badge>
										)}
									</div>
								</div>
							<div className="flex flex-wrap gap-2 shrink-0">
									{candidate.linkedinUrl && (
										<Button
											variant="outline"
											size="sm"
											onClick={(e: any) => { e.stopPropagation(); (globalThis as any).window.open(candidate.linkedinUrl, '_blank'); }}
										>
											LinkedIn
										</Button>
									)}
									{candidate.email && (
										<Button
											variant="outline"
											size="sm"
											onClick={(e: any) => { e.stopPropagation(); handleGenerateLink(candidate.id); }}
										>
											<Link className="mr-1 h-3 w-3" />
											Generate Link
										</Button>
									)}
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			) : (
				<Card>
					<CardContent className="flex flex-col items-center justify-center py-16">
						<Users className="h-16 w-16 text-muted-foreground/50 mb-4" />
						<h3 className="text-lg font-medium mb-2">No candidates yet</h3>
						<p className="text-muted-foreground text-center max-w-md">
							Source candidates from hiring requests to see them here.
						</p>
					</CardContent>
				</Card>
			)}

			{/* Generate Link Dialog */}
			<Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Generate Candidate Link</DialogTitle>
						<DialogDescription>
							Create a link for this candidate to view the job description and upload their CV.
						</DialogDescription>
					</DialogHeader>
					{loadingExisting ? (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
						</div>
					) : existingLink ? (
						<div className="py-4">
							<p className="text-sm font-medium mb-2">Active link found:</p>
							<div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
								<Input value={existingLink.url} readOnly className="flex-1 text-xs" />
								<Button
									size="sm"
									variant="outline"
									onClick={() => handleCopyLink(existingLink.url)}
								>
									{copiedToken === existingLink.url ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
								</Button>
							</div>
							<p className="text-xs text-muted-foreground mt-2">
								Expires {new Date(existingLink.expiresAt).toLocaleString()}
							</p>
							<DialogFooter className="mt-4">
								<Button variant="outline" onClick={() => { setExistingLink(null); }}>Generate New</Button>
								<Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Done</Button>
							</DialogFooter>
						</div>
					) : !generatedLink ? (
						<>
							<div className="grid gap-4 py-4">

								<p className="text-sm text-muted-foreground">
									Link expires in 48 hours. Candidate can upload CV once per link.
								</p>
							</div>
							<DialogFooter>
								<Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
								<Button onClick={handleConfirmGenerate} disabled={generateLinkMutation.isPending}>
									{generateLinkMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
									Generate Link
								</Button>
							</DialogFooter>
						</>
					) : (
						<div className="py-4">
							<div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
								<Input value={generatedLink} readOnly className="flex-1 text-xs" />
								<Button
									size="sm"
									variant="outline"
									onClick={() => handleCopyLink(generatedLink)}
								>
									{copiedToken === generatedLink ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
								</Button>
							</div>
							<p className="text-xs text-muted-foreground mt-2">Share this link with the candidate.</p>
							<DialogFooter className="mt-4">
								<Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Done</Button>
							</DialogFooter>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
